import { ITEM_LOCATION_TYPES, ROADMAP_TYPES } from '@/roadmaps/constants/roadmaps.constants';
import { calculateRoadmapTimeData } from '@/roadmaps/helpers/roadmaps-date.helper';
import store from '@/vuex/store';
import each from 'lodash/each';
import has from 'lodash/has';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import omit from 'lodash/omit';

export function freezeRoadmap (roadmap) {
    delete roadmap.items;
    delete roadmap.swimlanes;

    return Object.freeze({
        ...roadmap,
    });
}

export function roadmapObjectFromResponse (roadmapResponseData) {
    const swimlanes = multipleSwimlaneObjectsFromResponse(roadmapResponseData.swimlanes);

    switch (roadmapResponseData.type) {
        case ROADMAP_TYPES.TIMELINE: {
            const currentRoadmap = store.getters['roadmaps/roadmaps/currentRoadmap'];

            let roadmapStartDate = currentRoadmap?.startDate;
            let roadmapEndDate = currentRoadmap?.endDate;
            let timePeriodLabels = currentRoadmap?.timePeriodLabels;

            const alreadyHasTimeData = !!roadmapStartDate && !!roadmapEndDate && !!timePeriodLabels;
            if (!alreadyHasTimeData) {
                const {
                    roadmapStartDate: newRoadmapStartDate,
                    roadmapEndDate: newRoadmapEndDate,
                    timePeriodLabels: newTimePeriodLabels,
                } = calculateRoadmapTimeData({
                    items: roadmapResponseData.items,
                    quarterOneStartingMonth: roadmapResponseData.quarterOneStartingMonth,
                });

                roadmapStartDate = newRoadmapStartDate;
                roadmapEndDate = newRoadmapEndDate;
                timePeriodLabels = newTimePeriodLabels;
            }

            const items = roadmapResponseData.items?.length
                ? multipleItemObjectsFromResponse(roadmapResponseData.items, roadmapResponseData.type)
                : [];

            return Object.assign({}, roadmapResponseData, {
                timePeriodLabels,
                startDate: roadmapStartDate,
                endDate: roadmapEndDate,
                swimlanes,
                items,
            });
        }

        case ROADMAP_TYPES.COLUMN: {
            const columns = multipleColumnObjectsFromResponse(roadmapResponseData.columns);

            const items = roadmapResponseData.items?.length
                ? multipleItemObjectsFromResponse(roadmapResponseData.items, roadmapResponseData.type)
                : [];

            return Object.assign({}, roadmapResponseData, {
                swimlanes,
                columns,
                items,
            });
        }
    }
}

export function multipleSwimlaneObjectsFromResponse (responseSwimlanes) {
    return keyBy(
        map(responseSwimlanes, (swimlane, index) => {
            return {
                ...swimlane,
                index,
            };
        }),
        'id',
    );
}

export function multipleColumnObjectsFromResponse (responseColumns) {
    return keyBy(
        map(responseColumns, (column, index) => {
            return {
                ...column,
                index,
            };
        }),
        'id',
    );
}

export function singleColumnObjectFromResponse (responseColumn) {
    const { index } = store.getters['roadmaps/columns/columnById'](responseColumn.id);

    return {
        ...responseColumn,
        index,
    };
}

export function multipleItemObjectsFromResponse (
    responseItems,
    roadmapType = store.getters['roadmaps/roadmaps/currentRoadmap'].type,
) {
    const parentChildCount = {};

    const items = keyBy(
        map(responseItems, (item) => {
            const isChild = item.parentType === ITEM_LOCATION_TYPES.INITIATIVE.key;

            if (isChild) {
                parentChildCount[item.parentId] ||= 0;
                parentChildCount[item.parentId] += 1;
            }

            return {
                ...(store.getters['roadmaps/items/itemById'](item.id) || {}),
                ...singleItemObjectFromResponse(item, roadmapType),
            };
        }),
        'id',
    );

    return each(items, (item) => {
        const hasChildren = (parentChildCount[item.id] || 0) > 0;
        if (!hasChildren || !has(item, 'isExpanded')) {
            item.isExpanded = false;
        }
    });
}

export function singleItemObjectFromResponse (
    responseItem,
    roadmapType = store.getters['roadmaps/roadmaps/currentRoadmap'].type,
) {
    switch (roadmapType) {
        case ROADMAP_TYPES.TIMELINE: {
            const isCurrentlyExpanded = !!store.getters['roadmaps/items/itemById'](responseItem.id)?.isExpanded;
            const isExpanded = isCurrentlyExpanded && responseItem.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key;

            return Object.assign({}, responseItem, {
                isExpanded,
            });
        }
        case ROADMAP_TYPES.COLUMN:
            return omit(responseItem, ['startDate', 'endDate']);
    }
}

export function payloadWithRoadmapVersionTimestamp (payload) {
    const roadmapVersionTimestamp = store.getters['roadmaps/roadmaps/currentRoadmapVersionTimestamp'];

    return {
        roadmapVersionTimestamp,
        ...payload,
    };
}

export function updateRoadmapCreatedUpdatedInfoFromResponse (roadmapCreatedUpdatedInfo) {
    store.dispatch('roadmaps/roadmaps/setCurrentRoadmapCreatedUpdatedInfo', roadmapCreatedUpdatedInfo);
}

export function updateRoadmapPublishedInfoFromResponse (publishedInfo) {
    store.dispatch('roadmaps/roadmaps/setCurrentRoadmapPublishInfo', publishedInfo);
}
